import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, FileText, Loader2 } from "lucide-react";
import { WhatsAppGlyph } from "@/components/cards/CardShell";
import { fetchListSends, listsUrl, logListSend } from "@/lib/bf";
import { waLink } from "@/lib/wa";
import BFListSalon from "./BFListSalon";
import BFListHistory from "./BFListHistory";

const fname = (name) => `Lista_Preturi_${name.replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;

export default function BFListShare({ cfg, agent, url, admin = false }) {
  const brands = (cfg.brands || []).filter((b) => cfg.campaigns?.some((c) => c.brand === b.id && c.category === "oferta"));
  const [sel, setSel] = useState(() => brands.slice(0, 1).map((b) => b.id));
  const [files, setFiles] = useState({});
  const [salon, setSalon] = useState(null);
  const [hist, setHist] = useState([]);
  useEffect(() => { fetchListSends().then(setHist).catch(() => {}); }, []);
  useEffect(() => {
    sel.filter((id) => !files[id]).forEach((id) => {
      const name = brands.find((b) => b.id === id)?.name || id;
      fetch(listsUrl(`/${id}.pdf`)).then((r) => r.blob()).then((b) => setFiles((f) => ({ ...f, [id]: new File([b], fname(name), { type: "application/pdf" }) }))).catch(() => {});
    });
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!brands.length) return null;
  const picked = brands.filter((b) => sel.includes(b.id));
  const ready = picked.length > 0 && picked.every((b) => files[b.id]);
  const all = sel.length === brands.length;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const hello = salon?.admin_name ? `Bună ziua, ${salon.admin_name}!` : "Bună ziua!";
  const link = salon ? `${url}${url.includes("?") ? "&" : "?"}s=${salon.id}` : url;
  const text = `${hello} Vă trimit ${picked.length === 1 ? `lista de prețuri ${picked[0].name}` : "listele de prețuri"}, cu prețurile de Black Friday:\n`
    + picked.map((b) => `• ${b.name}: ${listsUrl(`/${b.id}`)}`).join("\n")
    + `\nComandă aici: ${link}${agent?.name ? `\n${agent.name}${agent.phone ? ` · ${agent.phone}` : ""}` : ""}`;
  const direct = salon && waLink(salon.phone, text);
  const log = () => logListSend({ lead: salon?.id || "", brands: picked.map((b) => b.id) }).then((d) => setHist((h) => [d, ...h])).catch(() => {});
  const send = () => {
    if (salon && !direct) return toast.error("Salonul nu are un număr de telefon valid");
    if (direct) { window.open(direct, "_blank", "noopener"); log(); return; }
    const fs = picked.map((b) => files[b.id]).filter(Boolean);
    if (fs.length && navigator.canShare?.({ files: fs })) {
      navigator.share({ files: fs, text }).then(log).catch((e) => { if (e?.name !== "AbortError") toast.error("Nu am putut deschide trimiterea"); });
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    log();
  };
  const names = Object.fromEntries((cfg.brands || []).map((b) => [b.id, b.name]));
  return (
    <div className="bf-picker" data-testid="bf-list-share">
      <div className="bf-picker-head">
        <b>Trimite liste de prețuri pe WhatsApp ({picked.length} din {brands.length})</b>
        <button type="button" className="bf-gate-switch" onClick={() => setSel(all ? [] : brands.map((b) => b.id))} data-testid="bf-list-share-all">{all ? "Deselectează tot" : "Selectează toate"}</button>
      </div>
      <div className="bf-picker-list">
        {brands.map((b) => {
          const on = sel.includes(b.id);
          return (
            <button key={b.id} type="button" className={`bf-picker-chip ${on ? "is-on" : ""}`} onClick={() => toggle(b.id)} aria-pressed={on} data-testid={`bf-list-share-${b.id}`}>
              {on && <Check size={13} />} {b.name}
            </button>
          );
        })}
      </div>
      <BFListSalon value={salon} onPick={setSalon} admin={admin} />
      <div className="share-actions">
        <button type="button" className="st-btn st-btn--light bf-agent-wa" disabled={!picked.length} onClick={send} data-testid="bf-list-share-send">
          {ready || !picked.length || direct ? <WhatsAppGlyph /> : <Loader2 size={14} className="animate-spin" />} Trimite {picked.length === 1 ? "lista" : `${picked.length} liste`} {salon ? `la ${salon.salon}` : "pe WhatsApp"}
        </button>
        {picked.length === 1 && <a className="st-btn st-btn--ghost" href={listsUrl(`/${picked[0].id}.pdf`)} target="_blank" rel="noopener noreferrer" data-testid="bf-list-share-open"><FileText size={14} /> Deschide PDF</a>}
      </div>
      <p className="st-hint">Alegi brandurile și, opțional, salonul: WhatsApp se deschide direct în conversația cu salonul, cu mesajul și linkurile gata scrise. Fără salon ales, pe telefon se trimit PDF-urile și alegi tu contactul.</p>
      <BFListHistory rows={hist} names={names} lead={salon?.id} />
    </div>
  );
}
